<script setup>
import CmsText from '@/@core/components/CmsText.vue'

/*
 * RM-318 (Kapil + product advisor 2026-09-28): the one page a member's
 * Review & Submit text links to — "Details: reserverm.com/request-details-faqs" — and
 * the FAQs link in the help line at the end of every RM AI text.
 * It shows the same words the Request screens show for the guaranteed
 * minimum and card hold, the cancellation policy and the RM Member
 * Commitment (same i18n keys, so CMS edits land here too), then the member
 * FAQs (same CMS keys as /members). Public, and deliberately not linked from
 * the site navigation.
 */
</script>

<template>
  <div class="request-details">
    <h1 class="request-details__title">
      Requests via RM
    </h1>

    <section class="request-details__section">
      <!-- The same name as the Request screens' section (RM-360) -->
      <h2 class="request-details__heading">
        How guaranteed F&amp;B spend works
      </h2>
      <p>A {{ $t('reservation.payment.holdDescription') }}</p>
      <p class="request-details__sub">
        {{ $t('reservation.payment.whatIfBelowMin') }}
      </p>
      <p>{{ $t('reservation.payment.restaurantMayEither', { restaurant: 'The restaurant' }) }}</p>
      <ul>
        <li>{{ $t('reservation.payment.chargeFlatFee', { amount: 'your guaranteed amount' }) }}</li>
        <li>{{ $t('reservation.payment.receiveRemainder') }}</li>
      </ul>
      <p>RM takes no commission or fees</p>
    </section>

    <section class="request-details__section">
      <h2 class="request-details__heading">
        {{ $t('reservation.payment.cancellationPolicy') }}
      </h2>
      <p>{{ $t('reservation.payment.fullRefund') }}</p>
      <p>{{ $t('reservation.payment.cancellationPenalty') }}</p>
    </section>

    <section class="request-details__section">
      <h2 class="request-details__heading">
        {{ $t('member.commitment.title') }}
      </h2>
      <div
        class="request-details__html"
        v-html="$t('member.commitment.bullets')"
      />
    </section>

    <section class="request-details__section">
      <h2 class="request-details__heading">
        <CmsText
          k="members.tabs.faqsTitle"
          tag="span"
        />
      </h2>
      <CmsText
        k="members.tabs.faqs"
        tag="div"
        class="request-details__html request-details__faqs"
      />
    </section>
  </div>
</template>

<style scoped>
.request-details {
  max-width: 720px;
  margin: 0 auto;
  padding: 32px 16px 64px;
  color: var(--ds-brand-ink, rgba(var(--v-theme-on-surface), 0.9));
  font-size: 16px;
  line-height: 1.55;
}
.request-details__title {
  font-family: var(--heading-font, 'Playfair Display', serif);
  font-weight: 500;
  font-size: 32px;
  margin: 0 0 24px;
}
.request-details__section {
  padding: 20px 0;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
.request-details__heading {
  font-family: var(--heading-font, 'Playfair Display', serif);
  font-weight: 500;
  font-size: 22px;
  margin: 0 0 8px;
}
.request-details__sub {
  font-weight: 600;
  margin-bottom: 4px;
}
.request-details p,
.request-details :deep(p) {
  margin: 0 0 8px;
}
.request-details ul,
.request-details :deep(ul) {
  margin: 0 0 8px;
  padding-left: 20px;
  list-style: disc;
}
@media (max-width: 600px) {
  .request-details__title { font-size: 24px; }
  .request-details__heading { font-size: 20px; }
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: Auth
</route>
